import React, { useState } from "react";
import { TopBar } from "@/components/TopBar";
import { MainAction } from "@/components/MainAction";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { ApiError, submitLead, type LeadInterest, type LeadSegment, type LeadVolume } from "@/lib/api";
import { canRequestPhone, getTelegramUser, haptic, requestPhone } from "@/lib/telegram";

const CITIES = ["Ташкент", "Самарканд", "Бухара", "Наманган", "Андижан", "Фергана", "Нукус", "Карши", "Термез", "Ургенч", "Другой"];
const INTERESTS: LeadInterest[] = ["beans", "equipment", "training", "service"];
const VOLUMES: LeadVolume[] = ["до 5 кг", "5–20 кг", "20+ кг", "не знаю"];
const ic = (d: string) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />;
const SEG_ICON: Record<LeadSegment, React.ReactNode> = {
  horeca: ic('<path d="M5 11h11v3a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5zM16 12h1.5a2.5 2.5 0 0 1 0 5H15M8 4v4M11 3v5M14 4v4"/>'),
  home_office: ic('<path d="M4 11 12 4l8 7M6 10v9h12v-9"/>'),
  other: ic('<circle cx="12" cy="12" r="8"/><path d="M8.5 12h.01M12 12h.01M15.5 12h.01"/>'),
};

/**
 * Lead form for cafés, offices and others. The first question ("who are
 * you") shapes the rest; the phone comes from Telegram in one tap.
 */
export function LeadPage({ source = "app" }: { source?: string }) {
  const { t } = useI18n();
  const { goBack, navigate } = useRouter();
  useBackButton(goBack);
  const tgUser = getTelegramUser();
  const [segment, setSegment] = useState<LeadSegment>("horeca");
  const [fullName, setFullName] = useState([tgUser?.first_name, tgUser?.last_name].filter(Boolean).join(" "));
  const [city, setCity] = useState("Ташкент");
  const [orgName, setOrgName] = useState("");
  const [phone, setPhone] = useState("");
  const [canTelegram, setCanTelegram] = useState(!!tgUser?.username);
  const [interests, setInterests] = useState<LeadInterest[]>(["beans"]);
  const [volume, setVolume] = useState<LeadVolume | undefined>();
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const needsOrg = segment === "horeca";
  const phoneOk = phone.replace(/\D/g, "").length >= 9;
  const valid = fullName.trim().length >= 2 && phoneOk && (!needsOrg || orgName.trim().length > 0) && consent;

  const submit = async () => {
    if (done) return navigate({ name: "catalog" });
    if (!valid) {
      setShowErrors(true);
      haptic("error");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitLead({
        segment,
        fullName: fullName.trim(),
        city,
        orgName: orgName.trim() || undefined,
        phone: phone.trim(),
        canTelegram,
        interests,
        volume: segment === "other" ? undefined : volume,
        comment: comment.trim() || undefined,
        source: source.replace(/[^a-z0-9_-]/gi, "").toLowerCase().slice(0, 40) || "app",
      });
      haptic("success");
      setDone(true);
    } catch (err) {
      haptic("error");
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <TopBar title={t("lead.title")} onBack={goBack} showCart={false} />
        <main className="page page--no-nav" style={{ textAlign: "center", paddingTop: 48 }}>
          <div className="sheet__success-icon">✓</div>
          <h1 className="lead-h1">{t("lead.doneTitle")}</h1>
          <p className="lead-sub">{t("lead.doneText")}</p>
          <MainAction text={t("lead.toCatalog")} onClick={submit} />
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar title={t("lead.title")} onBack={goBack} showCart={false} />
      <main className="page page--no-nav">
        <span className="eyebrow">{t("lead.eyebrow")}</span>
        <h1 className="lead-h1">{t("lead.h1")}</h1>
        <p className="lead-sub">{t("lead.sub")}</p>

        <div className="field" style={{ marginBottom: 8 }}>
          <label>{t("lead.who")}</label>
        </div>
        <div className="purpose-options">
          {(["horeca", "home_office", "other"] as LeadSegment[]).map((s) => (
            <button key={s} className="purpose-option" data-active={segment === s} onClick={() => { haptic("selection"); setSegment(s); }}>
              {SEG_ICON[s]}
              <span style={{ fontWeight: 600, color: "var(--color-ink)" }}>{t(`lead.seg.${s}`)}</span>
              <span style={{ fontSize: 11 }}>{t(`lead.segHint.${s}`)}</span>
            </button>
          ))}
        </div>

        <div className="field">
          <label htmlFor="ld-name">{t("lead.fullName")}</label>
          <input id="ld-name" value={fullName} autoComplete="name" onChange={(e) => setFullName(e.target.value)} aria-invalid={showErrors && fullName.trim().length < 2} />
        </div>
        <div className="lead-grid">
          <div className="field">
            <label htmlFor="ld-city">{t("lead.city")}</label>
            <select id="ld-city" value={city} onChange={(e) => setCity(e.target.value)}>
              {CITIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          {(
            <div className="field">
              <label htmlFor="ld-org">{segment === "horeca" ? t("lead.orgHoreca") : segment === "home_office" ? t("lead.orgOffice") : t("lead.orgOther")}</label>
              <input id="ld-org" value={orgName} onChange={(e) => setOrgName(e.target.value)} aria-invalid={showErrors && needsOrg && !orgName.trim()} />
            </div>
          )}
        </div>

        <div className="field" style={{ marginBottom: 8 }}>
          <label>{t("lead.contact")}</label>
        </div>
        {canRequestPhone() && (
          <button
            className="contact-btn"
            onClick={async () => {
              const p = await requestPhone();
              if (p) {
                haptic("success");
                setPhone(p);
              }
            }}
          >
            {ic('<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z"/>')}
            {t("growth.sharePhone")}
          </button>
        )}
        <div className="field">
          <input type="tel" inputMode="tel" autoComplete="tel" placeholder="+998 90 123 45 67" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s()-]/g, "").slice(0, 20))} aria-invalid={showErrors && !phoneOk} />
          {showErrors && !phoneOk && <span className="field__error">{t("inquiry.phoneInvalid")}</span>}
          {tgUser?.username && (
            <label className="consent" style={{ marginTop: 6 }}>
              <input type="checkbox" checked={canTelegram} onChange={(e) => setCanTelegram(e.target.checked)} />
              <span>{t("lead.canTelegram", { username: tgUser.username })}</span>
            </label>
          )}
        </div>

        <div className="field" style={{ marginBottom: 8 }}>
          <label>{t("lead.interest")}</label>
        </div>
        <div className="segmented" style={{ flexWrap: "wrap", marginBottom: 16 }}>
          {INTERESTS.map((i) => (
            <button key={i} className="chip" data-active={interests.includes(i)} onClick={() => setInterests((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]))}>
              {t(`lead.interests.${i}`)}
            </button>
          ))}
        </div>

        {segment !== "other" && (
          <>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>{t("lead.volume")}</label>
            </div>
            <div className="segmented" style={{ flexWrap: "wrap", marginBottom: 16 }}>
              {VOLUMES.map((v) => (
                <button key={v} className="chip" data-active={volume === v} onClick={() => setVolume(volume === v ? undefined : v)}>
                  {t(`lead.volumes.${VOLUMES.indexOf(v)}`)}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="field">
          <label htmlFor="ld-comment">{t("lead.comment")}</label>
          <textarea id="ld-comment" rows={2} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>

        <label className={`consent ${showErrors && !consent ? "consent--error" : ""}`} style={{ marginTop: 0 }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>
            {t("lead.consent")}{" "}
            <a href="#/p/privacy" onClick={(e) => { e.preventDefault(); navigate({ name: "post", slug: "privacy" }); }}>
              {t("checkout.privacyLink")}
            </a>
          </span>
        </label>
        {error && <div className="form-error-banner">{error}</div>}
        <MainAction text={t("lead.submit")} onClick={submit} loading={busy} />
      </main>
    </>
  );
}
